import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { CheckIcon } from '@heroicons/react/24/solid'
import { useComponentTranslation } from '@tebuto/functions/translation.components'
import { cn } from '@tebuto/lib/utils'
import { Popover, PopoverContent, PopoverTrigger } from '@tebuto/widget/popover'
import { useState } from 'react'

export interface FilterOption {
    id: number
    name: string
}

interface FilterMultiSelectProps {
    label: string
    options: FilterOption[]
    selectedIds: number[]
    onSelectionChange: (ids: number[]) => void
    placeholder?: string
}

export default function FilterMultiSelect({
    label,
    options,
    selectedIds,
    onSelectionChange,
    placeholder
}: FilterMultiSelectProps) {
    const { t } = useComponentTranslation()
    const [open, setOpen] = useState(false)

    const selectedOptions = options.filter((opt) => selectedIds.includes(opt.id))

    const toggleOption = (id: number) => {
        if (selectedIds.includes(id)) {
            onSelectionChange(selectedIds.filter((selectedId) => selectedId !== id))
        } else {
            onSelectionChange([...selectedIds, id])
        }
    }

    const selectAll = () => {
        onSelectionChange(options.map((opt) => opt.id))
    }

    const clearAll = () => {
        onSelectionChange([])
    }

    return (
        <div className="space-y-3">
            <h4 className="font-medium text-sm widget-text-primary">{label}</h4>

            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        className="w-full min-h-[42px] px-3 py-2 text-left widget-bg-field widget-border border rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-(--widget-primary) transition-colors"
                    >
                        <div className="flex items-center justify-between gap-2">
                            <div className="flex-1 min-h-[24px] flex items-center">
                                {selectedOptions.length === 0 ? (
                                    <span className="text-xs widget-text-secondary opacity-60">{placeholder}</span>
                                ) : selectedOptions.length === options.length ? (
                                    <span className="text-xs widget-text-primary">
                                        {t('booking.bookingCalendar.allSelected')}
                                    </span>
                                ) : (
                                    <span className="text-xs widget-text-primary">
                                        {t('booking.bookingCalendar.nSelected', { count: selectedOptions.length })}
                                    </span>
                                )}
                            </div>
                            <ChevronDownIcon
                                className={cn(
                                    'size-4 widget-text-secondary shrink-0 transition-transform duration-200',
                                    open && 'rotate-180'
                                )}
                            />
                        </div>
                    </button>
                </PopoverTrigger>

                <PopoverContent
                    side="bottom"
                    align="start"
                    className="w-(--radix-popover-trigger-width) p-0 widget-bg-primary widget-border border rounded-lg shadow-sm overflow-hidden"
                    sideOffset={4}
                >
                    {/* Quick actions */}
                    <div className="flex items-center justify-between px-3 py-2 border-b widget-border">
                        <button
                            type="button"
                            onClick={selectAll}
                            className="text-xs widget-text-secondary hover:widget-text-primary transition-colors"
                        >
                            {t('booking.bookingCalendar.selectAll')}
                        </button>
                        <button
                            type="button"
                            onClick={clearAll}
                            className="text-xs widget-text-secondary hover:widget-text-primary transition-colors"
                        >
                            {t('booking.bookingCalendar.deselectAll')}
                        </button>
                    </div>

                    {/* Options list */}
                    <div className="max-h-[200px] overflow-y-auto">
                        {options.map((option) => {
                            const isSelected = selectedIds.includes(option.id)
                            return (
                                <button
                                    key={option.id}
                                    type="button"
                                    onClick={() => toggleOption(option.id)}
                                    className="w-full px-3 py-2.5 flex items-center gap-3 text-left transition-colors hover:bg-(--widget-primary-50)"
                                    style={isSelected ? { backgroundColor: 'var(--widget-primary-25)' } : undefined}
                                >
                                    <div
                                        className="size-4 rounded border flex items-center justify-center shrink-0 transition-colors"
                                        style={
                                            isSelected
                                                ? {
                                                      backgroundColor: 'var(--widget-primary-600)',
                                                      borderColor: 'transparent'
                                                  }
                                                : {
                                                      borderColor: 'var(--widget-border)',
                                                      backgroundColor: 'hsla(0, 0%, 100%, 0.5)'
                                                  }
                                        }
                                    >
                                        {isSelected && <CheckIcon className="size-3 text-white" />}
                                    </div>
                                    <span className="text-xs widget-text-primary">{option.name}</span>
                                </button>
                            )
                        })}
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    )
}
